// コネクタ（接続矢印）の作成 UI。
//
// 状態は canvas-store の connectionDraft で管理する。
// 1. 図形を単一選択中: 4 辺の少し外に接続開始ドットを表示する。ホバーで矢印アイコンへ拡大。
// 2. ドットを pointerdown: 接続モード開始。ポインタへゴースト線を引く（ドラッグ / クリック両対応）。
// 3. 接続先候補（図形の AABB 内）に入ると、その図形へ吸着して 4 辺ドットを表示し、最寄り辺へスナップ。
// 4. 接続先で pointerup（ドラッグ）/ もう一度クリック（クリック操作）で確定し、コネクタを作成する。
//    Esc でキャンセル。
// 5. 接続先が無い空白で確定した場合は、接続元と同じ種別のオブジェクト（付箋 → 付箋 /
//    テキスト → テキスト / 図形 → 同じ shapeKind）を矢印の先へ自動作成し、コネクタで接続する
//    （connector-auto-create.ts）。作成予定のオブジェクトは、ドラッグ中およびクリック確定待ち
//    （armed）中に半透明のゴーストとして予告表示する。
//    画像・動画・リンクカード・スタンプからの自動作成は行わず、従来どおりキャンセルする。
//
// 確定時の判定順（既存の接続作成経路を退行させないための不変条件）:
//   0. connectionDraft が既に null（Esc の resetMode など）なら後始末だけして何もしない
//   1. 接続先があれば従来どおりコネクタのみを作成して終了する。
//      自動作成のために新設した抑止条件（キャンバス面判定・非主ボタン・ツール切替）は
//      この経路へは一切適用しない
//   2. 接続先が無い場合にのみ、自動作成の可否を判定する

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  isConnectorEndpointObject,
  type CanvasFrame,
  type CanvasObject,
  type ConnectorSide,
} from "@refinement-canvas/domain";
import { ArrowOutwardIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useCanvasStore, type CanvasTool, type ConnectionDraftState } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import {
  buildAutoCreateDrafts,
  computeAutoCreateFrame,
  isAutoCreateBlocked,
} from "./connector-auto-create";
import { isPointOnCanvasSurface } from "./canvas-surface-hit";
import { STICKY_DEFAULT_BACKGROUND } from "./objects/sticky-object";
import { SHAPE_DEFAULT_STROKE, SHAPE_DEFAULT_STROKE_WIDTH } from "../../lib/shape-preference";
import { generateCanvasObjectId } from "../../lib/canvas-object-id";
import { useConnectorDragStore } from "./store/connector-drag-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { screenToStage } from "../../lib/canvas-coordinates";
import {
  arrowHeadPoints,
  buildRoundedPath,
  computeConnectorRoute,
  nearestSide,
  sideAnchor,
  sideUnitVector,
  CONNECTOR_TARGET_HIT_PAD_PX,
  type ConnectorFrame,
  type ConnectorPoint,
} from "../../lib/connector-geometry";

// 接続開始ドットを辺の外側へ離す距離（ステージ座標 px）。
const DOT_OFFSET_PX = 18;
// クリックとドラッグを区別する移動量しきい値（クライアント px）。
const CLICK_THRESHOLD_PX = 4;
const ALL_SIDES: readonly ConnectorSide[] = ["top", "right", "bottom", "left"];
const ARROW_SIZE = 12;
const CORNER_RADIUS = 10;

function frameOf(object: CanvasObject): ConnectorFrame {
  return { x: object.x, y: object.y, w: object.w, h: object.h, rotation: object.rotation ?? 0 };
}

// コネクタの接続元 / 接続先になりうるオブジェクトか。
// 種別の可否はサーバ検証と同じ正本（domain の isConnectorEndpointObject。ペンストローク・
// コネクタ・コメントスレッド・セクションを除外）へ委譲し、UI 固有の条件（ロック中は接続
// 操作をさせない）だけをここで足す。種別追加時に UI とサーバの契約がずれるのを防ぐ。
function isConnectableObject(object: CanvasObject | undefined): object is CanvasObject {
  return object !== undefined && !object.locked && isConnectorEndpointObject(object);
}

// 指定ステージ点を含む最前面の接続先オブジェクトと、その最寄り辺を返す。見つからなければ null。
function findConnectTarget(
  fromObjectId: string,
  point: ConnectorPoint
): { objectId: string; side: ConnectorSide } | null {
  const objects = useCanvasDocStore.getState().objects;
  let best: CanvasObject | null = null;
  for (const object of Object.values(objects)) {
    if (object.id === fromObjectId) continue;
    // 接続可能種別（ペン・コネクタ・コメントスレッド・ロックを除外）のみを接続先候補にする。
    if (!isConnectableObject(object)) continue;
    const within =
      point.x >= object.x - CONNECTOR_TARGET_HIT_PAD_PX &&
      point.x <= object.x + object.w + CONNECTOR_TARGET_HIT_PAD_PX &&
      point.y >= object.y - CONNECTOR_TARGET_HIT_PAD_PX &&
      point.y <= object.y + object.h + CONNECTOR_TARGET_HIT_PAD_PX;
    if (!within) continue;
    if (!best || object.zOrder > best.zOrder) best = object;
  }
  if (!best) return null;
  return { objectId: best.id, side: nearestSide(frameOf(best), point) };
}

export function CanvasConnectorOverlay({
  stageContainerRef,
}: {
  /**
   * キャンバス面（canvas-container）の参照。確定位置がキャンバス面の内側かどうかの判定に使う。
   * テスト用の data-testid ではなく実体の参照を受け取ることで、目印の改名で判定が静かに壊れないようにする。
   */
  readonly stageContainerRef: React.RefObject<HTMLElement | null>;
}): JSX.Element | null {
  const dispatcher = useCanvasOpDispatcherContext();
  const connectionDraft = useCanvasStore((s) => s.connectionDraft);
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);
  const tool = useCanvasStore((s) => s.tool);
  const objects = useCanvasDocStore((s) => s.objects);
  // 接続開始ドット（選択時のみ表示）は、選択枠・ハンドルと同様にズーム倍率へ依存しない
  // 画面上一定サイズにする。アンカーからの外側オフセットは 1/zoom で補正し、ドット本体は
  // transform: scale(1/zoom) で（内包する矢印アイコンごと）画面上一定サイズに描く。
  const zoom = useCanvasStore((s) => s.view.zoom);
  const k = 1 / zoom;
  // 単一選択中のオブジェクトを移動 / リサイズ / 回転している最中も接続ドットを追従させるため、
  // 確定前のライブ枠を購読する（無操作時は undefined のままで再描画は発生しない）。
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : undefined;
  const selectedLive = useConnectorDragStore((s) => (selectedId ? s.frames[selectedId] : undefined));

  // 進行中の接続操作を中断するための関数を保持し、アンマウント時に確実に後始末する
  // （window へ張ったリスナと connectionDraft の取り残しを防ぐ）。
  const activeCancelRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      activeCancelRef.current?.();
      activeCancelRef.current = null;
    };
  }, []);

  // 接続先が無い状態で確定したときに、接続元と同じ種別のオブジェクトとコネクタを自動作成する。
  // 何も作らない条件（キャンバス面外・ツール切替・接続元の消失/ロック/対象外種別・
  // 既存オブジェクトの上）に該当した場合は静かに何もしない。
  const autoCreateAtDraft = useCallback(
    (draft: ConnectionDraftState): void => {
      // キャンバス面の外（ツールバー / ミニマップ / メニュー / コメントピンの上）では作らない。
      // ここで作ってしまうと、特にミニマップ上のリリースで画面外の遠方にオブジェクトが湧く。
      if (!draft.onCanvasSurface) return;
      // ドラフト中に配置ツール等へ切り替わっていたら作らない
      // （配置ツールのクリック配置と二重に生成されたように見えるため）。
      if (useCanvasStore.getState().tool !== "idle") return;
      const objects = useCanvasDocStore.getState().objects;
      // 接続元は確定時点で取り直す。ドラッグ中に他参加者が削除・ロック・移動している可能性があり、
      // 開始時のスナップショットで計算すると存在しない端点や古い位置を使ってしまう。
      const source = objects[draft.fromObjectId];
      const newObjectId = generateCanvasObjectId();
      const drafts = buildAutoCreateDrafts({
        source,
        fromSide: draft.fromSide,
        dropPoint: draft.pointer,
        gridSnapEnabled: useCanvasStore.getState().gridSnapEnabled,
        newObjectId,
        objects,
      });
      if (drafts.length === 0) return;
      // 端点図形 → コネクタの順で 1 バッチ作成する（サーバの端点存在検証を満たす送信順であり、
      // 1 回の Undo で両方が取り消される）。
      const created = dispatcher.createObjects(drafts);
      // オブジェクト数上限などで 1 件も作られなかった場合は、選択・編集モードを変更しない。
      // drafts の並び順（端点 → コネクタ）へ暗黙に依存せず、採番した id で引く。
      const createdObject = created.find((object) => object.id === newObjectId);
      if (!createdObject) return;
      useCanvasStore.getState().setSelectedObjectIds([createdObject.id]);
      if (createdObject.type === "sticky" || createdObject.type === "text") {
        // ツールバーからの配置と同じく、作成直後に文字を入力できる状態にする。
        useCanvasStore.getState().setAutoEditObjectId(createdObject.id);
      }
    },
    [dispatcher]
  );

  // ドット pointerdown → 接続モード開始。ドラッグ / クリックの両操作に対応する状態機械。
  const beginConnection = useCallback(
    (fromObjectId: string, fromSide: ConnectorSide, event: React.PointerEvent): void => {
      // 主ボタン（左）以外では接続を開始しない。中ボタン（キャンバス移動）や右ボタンが
      // ドット上に落ちても、接続ドラフトを開始せずパン / メニュー側へ委ねる。
      // マルチタッチの 2 本目以降（isPrimary === false）でも開始しない。connectionDraft は
      // 単一のため、副ポインタで開始すると進行中のドラフトを後勝ちで上書きしてしまう。
      // これは自動作成の抑止条件ではなく接続操作そのものの健全性ガードのため、既存の
      // 「既存オブジェクトへ接続する」経路にも等しく適用する（後述の onDown で既存経路へ
      // 適用しないと述べているのは、確定時のボタン条件だけを指す）。
      // jsdom など isPrimary を持たない環境では undefined になるため、明示的な false のみ弾く。
      if (event.button !== 0 || event.isPrimary === false) return;
      event.preventDefault();
      event.stopPropagation();
      const fromObj = useCanvasDocStore.getState().objects[fromObjectId];
      if (!fromObj) return;
      const startClientX = event.clientX;
      const startClientY = event.clientY;
      // 1 回のポインタ相互作用（この pointerdown 〜 pointerup / pointercancel）を識別する。
      // 別の指・別のポインタによる move / up / cancel を無視して、進行中の接続を守る。
      const startPointerId = event.pointerId;
      const anchor = sideAnchor(frameOf(fromObj), fromSide);
      useCanvasStore.getState().setConnectionDraft({
        fromObjectId,
        fromSide,
        pointer: anchor,
        target: null,
        // まだポインタ座標でのキャンバス面判定を行っていないため false から始める
        // （この時点ではゴースト枠を出さない）。
        onCanvasSurface: false,
      });

      let mode: "dragging" | "armed" = "dragging";
      // 直近のポインタのクライアント座標。ビュー（パン / ズーム）が変わったときに
      // 同じ画面位置からステージ座標を計算し直すために保持する。
      let lastClientX = startClientX;
      let lastClientY = startClientY;

      // 現在のクライアント座標から draft（pointer / target / onCanvasSurface）を更新し、
      // 見つかった target を返す。
      //
      // onCanvasSurface はここで毎回計算し直す。pointermove だけで更新すると、
      // タッチのタップ確定（pointermove が 1 度も発生しない）で初期値のまま確定してしまい、
      // ミニマップやツールバーの上でも「キャンバス面」と誤判定してしまう。
      const updateAt = (
        clientX: number,
        clientY: number
      ): { objectId: string; side: ConnectorSide } | null => {
        lastClientX = clientX;
        lastClientY = clientY;
        const view = useCanvasStore.getState().view;
        const pointer = screenToStage({ x: clientX, y: clientY }, view);
        const target = findConnectTarget(fromObjectId, pointer);
        const onCanvasSurface = isPointOnCanvasSurface(stageContainerRef.current, clientX, clientY);
        useCanvasStore
          .getState()
          .setConnectionDraft({ fromObjectId, fromSide, pointer, target, onCanvasSurface });
        return target;
      };

      // 接続中もホイール / WASD でのパン・ズームは可能なため、ビューが変わったら
      // 同じクライアント座標からドラフト（ポインタ位置・接続先・確定面）を計算し直す。
      // これを行わないと、パン後にポインタを動かさず離したとき、表示していたゴースト枠と
      // 実際に作成される位置がずれる（確定時は必ず現在のビューで再計算されるため）。
      const unsubscribeView = useCanvasStore.subscribe((state, prev) => {
        if (state.view === prev.view) return;
        updateAt(lastClientX, lastClientY);
      });
      const cleanup = (): void => {
        unsubscribeView();
        window.removeEventListener("pointermove", onMove, true);
        window.removeEventListener("pointerup", onUp, true);
        window.removeEventListener("pointerdown", onDown, true);
        window.removeEventListener("pointercancel", onCancel, true);
        window.removeEventListener("keydown", onKey, true);
        activeCancelRef.current = null;
      };
      const finish = (commit: boolean): void => {
        const draft = useCanvasStore.getState().connectionDraft;
        useCanvasStore.getState().setConnectionDraft(null);
        cleanup();
        // Esc の resetMode など他経路で既にドラフトが消えている場合は後始末だけで終える。
        if (!commit || !draft) return;
        if (draft.target) {
          // 接続先あり: 従来どおりコネクタのみを作成する（既存挙動を一切変えない）。
          const created = dispatcher.createConnector(
            { objectId: draft.fromObjectId, side: draft.fromSide },
            draft.target
          );
          if (created) useCanvasStore.getState().setSelectedObjectIds([created.id]);
          return;
        }
        // 接続先なし: 接続元と同じ種別のオブジェクトを矢印の先へ自動作成する。
        autoCreateAtDraft(draft);
      };
      const onMove = (ev: PointerEvent): void => {
        // 別のポインタ（マルチタッチの 2 本目など）の移動では draft を動かさない。
        if (ev.pointerId !== startPointerId) return;
        updateAt(ev.clientX, ev.clientY);
      };
      const onCancel = (ev: PointerEvent): void => {
        // 無関係なポインタの pointercancel で進行中の接続を中断しない。
        if (ev.pointerId !== startPointerId) return;
        finish(false);
      };
      const onUp = (ev: PointerEvent): void => {
        if (mode !== "dragging") return;
        if (ev.pointerId !== startPointerId) return;
        const target = updateAt(ev.clientX, ev.clientY);
        if (target) {
          finish(true);
          return;
        }
        const moved =
          Math.hypot(ev.clientX - startClientX, ev.clientY - startClientY) > CLICK_THRESHOLD_PX;
        if (moved) {
          // ドラッグして空白で離した → 自動作成を試みる（抑止条件に該当すれば何も作らない）。
          finish(true);
          return;
        }
        // 移動なしのクリック → armed モードへ。ここでは finish を呼ばない
        // （呼ぶとドット 1 回クリックだけでオブジェクトが作られてしまう）。
        mode = "armed";
        window.addEventListener("pointerdown", onDown, true);
      };
      const onDown = (ev: PointerEvent): void => {
        // armed モード: クリック位置に接続先があれば従来どおり接続、無ければ自動作成を試みる。
        ev.preventDefault();
        ev.stopPropagation();
        const target = updateAt(ev.clientX, ev.clientY);
        if (target) {
          // 接続先あり: 従来どおり確定する。自動作成のために新設したボタン条件は、
          // 既存の接続作成経路へは適用しない（変更前はボタン種別を見ずに確定していた）。
          finish(true);
          return;
        }
        // 接続先なし（＝自動作成の分岐）でのみ、主ボタン以外・副ポインタを弾く。
        // armed の 2 回目は別のポインタ相互作用のため pointerId の一致は要求しない
        // （タッチはタップごとに pointerId が採番されるため、要求するとクリック接続が成立しない）。
        if (ev.button !== 0 || ev.isPrimary === false) {
          finish(false);
          return;
        }
        finish(true);
      };
      const onKey = (ev: KeyboardEvent): void => {
        if (ev.key === "Escape") finish(false);
      };

      activeCancelRef.current = () => finish(false);
      window.addEventListener("pointermove", onMove, true);
      window.addEventListener("pointerup", onUp, true);
      window.addEventListener("pointercancel", onCancel, true);
      window.addEventListener("keydown", onKey, true);
    },
    [dispatcher, autoCreateAtDraft, stageContainerRef]
  );

  if (connectionDraft) {
    return <ConnectionDraftView draft={connectionDraft} objects={objects} />;
  }

  // 接続開始ドットは選択モード（idle）でのみ表示する。付箋 / 図形 / テキスト / スタンプ配置や
  // ペン描画ツールの選択中は、ドット付近のクリックが接続開始へ化けて配置・描画を阻害するため出さない。
  if (tool !== "idle") return null;
  if (selectedIds.length !== 1) return null;
  const selected = objects[selectedIds[0]!];
  if (!isConnectableObject(selected)) return null;

  // 操作中はライブ枠（確定前の位置・サイズ・回転）を優先し、ドットを図形に追従させる。
  const frame: ConnectorFrame = selectedLive
    ? {
        x: selectedLive.x,
        y: selectedLive.y,
        w: selectedLive.w,
        h: selectedLive.h,
        rotation: selectedLive.rotation ?? 0,
      }
    : frameOf(selected);
  const rotation = frame.rotation ?? 0;

  return (
    <div
      data-testid="canvas-connector-handles"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: Number.MAX_SAFE_INTEGER }}
    >
      {ALL_SIDES.map((side) => {
        const anchor = sideAnchor(frame, side);
        // 外向き方向は図形の回転に追従させる（ドットの外側オフセットと矢印アイコンの向きに使う）。
        const dir = sideUnitVector(side, rotation);
        return (
          <ConnectionStartDot
            key={side}
            side={side}
            x={anchor.x + dir.x * DOT_OFFSET_PX * k}
            y={anchor.y + dir.y * DOT_OFFSET_PX * k}
            dir={dir}
            scale={k}
            onStart={(s, e) => beginConnection(selected.id, s, e)}
          />
        );
      })}
    </div>
  );
}

// 接続開始ドット。ホバーで拡大し、その辺の外向き方向を指す矢印アイコンを表示する。
function ConnectionStartDot({
  x,
  y,
  side,
  dir,
  scale,
  onStart,
}: {
  readonly x: number;
  readonly y: number;
  readonly side: ConnectorSide;
  /** その辺の外向き単位ベクトル（図形の回転込み）。矢印アイコンの向きに使う。 */
  readonly dir: ConnectorPoint;
  /** ズーム逆補正の倍率（= 1/zoom）。ドット本体を画面上一定サイズにするための scale。 */
  readonly scale: number;
  readonly onStart: (side: ConnectorSide, event: React.PointerEvent) => void;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  const size = hover ? 22 : 12;
  // ArrowOutward アイコンは右上（北東 = -45°）向き。外向き方向 dir に合わせて回転させる。
  // 例: 右辺(1,0)→+45°回転で右向き(→)、左辺(-1,0)→左向き(←)、上辺(0,-1)→上向き(↑)、下辺(0,1)→下向き(↓)。
  const iconAngleDeg = (Math.atan2(dir.y, dir.x) * 180) / Math.PI + 45;
  return (
    <div
      data-testid={`connector-start-dot-${side}`}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onPointerDown={(event) => onStart(side, event)}
      title="ドラッグ（またはクリック）して矢印で接続"
      style={{
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
        height: `${size}px`,
        marginLeft: `${-size / 2}px`,
        marginTop: `${-size / 2}px`,
        // ドット本体（枠線・内包する矢印アイコンを含む）を 1/zoom 補正で画面上一定サイズに描く。
        // 中心（left/top + margin で位置決め済み）を基準に縮小するため、位置はズレない。
        transform: `scale(${scale})`,
        transformOrigin: "center",
        borderRadius: "50%",
        background: hover ? "var(--color-selection)" : "var(--color-surface)",
        border: "2px solid var(--color-selection)",
        color: "var(--color-surface)",
        cursor: "crosshair",
        pointerEvents: "auto",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
      }}
    >
      {hover ? (
        <span
          style={{ display: "grid", placeItems: "center", transform: `rotate(${iconAngleDeg}deg)` }}
        >
          <Icon d={ArrowOutwardIconData} />
        </span>
      ) : null}
    </div>
  );
}

/**
 * 自動作成が成立する場合に、これから作られるオブジェクトの枠を返す（成立しなければ null）。
 * 確定処理と同じ computeAutoCreateFrame / isAutoCreateBlocked を使うことで、
 * 「ゴーストで見えていた位置」と「実際に作られる位置」が構造的にずれないようにする。
 *
 * なお、オブジェクト数の上限判定はここでは行わない（上限時はゴーストが出たまま作成されず、
 * dispatcher 側のトーストで通知される）。UI 層へ上限判定を持ち込まないための意図的な割り切り。
 *
 * 受け入れたトレードオフ: 接続ドラッグ中は 1 イベントあたり findConnectTarget と
 * isAutoCreateBlocked の 2 本の全件走査が走る（従来は前者のみ）。いずれも矩形判定だけの
 * 軽い走査であり、オブジェクト数上限（10,000 件）でも再描画と同オーダーに収まる想定。
 * 実機で体感の劣化が見られた場合は、ゴースト側の判定だけを間引き（確定時は必ず計算する）、
 * それでも足りなければ走査結果を draft へ載せて 1 パスへ統合する。
 */
function autoCreateGhostFrame(
  draft: ConnectionDraftState,
  objects: Record<string, CanvasObject>,
  gridSnapEnabled: boolean,
  tool: CanvasTool
): CanvasFrame | null {
  if (draft.target) return null;
  if (!draft.onCanvasSurface) return null;
  if (tool !== "idle") return null;
  const source = objects[draft.fromObjectId];
  if (!source) return null;
  if (isAutoCreateBlocked({ source, dropPoint: draft.pointer, objects })) return null;
  return computeAutoCreateFrame({
    source,
    fromSide: draft.fromSide,
    dropPoint: draft.pointer,
    gridSnapEnabled,
  });
}

// 接続作成中のゴースト表示。接続先があれば折れ線ルート + 4 辺ドット、無ければ直線（破線）を描く。
// 接続先が無く自動作成が成立する場合は、これから作られるオブジェクトの枠も破線で描く。
function ConnectionDraftView({
  draft,
  objects,
}: {
  readonly draft: ConnectionDraftState;
  readonly objects: Record<string, CanvasObject>;
}): JSX.Element | null {
  // グリッド吸着はドラフト中に切り替えられる（G キー）ため、getState ではなく購読して
  // ゴースト枠を追随させる。
  const gridSnapEnabled = useCanvasStore((s) => s.gridSnapEnabled);
  const tool = useCanvasStore((s) => s.tool);
  const fromObj = objects[draft.fromObjectId];
  if (!fromObj) return null;
  const ghost = autoCreateGhostFrame(draft, objects, gridSnapEnabled, tool);
  const fromFrame = frameOf(fromObj);
  const anchor = sideAnchor(fromFrame, draft.fromSide);

  const targetObj = draft.target ? objects[draft.target.objectId] : undefined;

  let points: ConnectorPoint[];
  let dashed: boolean;
  if (draft.target && targetObj) {
    points = computeConnectorRoute(fromFrame, draft.fromSide, frameOf(targetObj), draft.target.side);
    dashed = false;
  } else {
    points = [anchor, draft.pointer];
    dashed = true;
  }
  const d = buildRoundedPath(points, CORNER_RADIUS);
  const tip = points[points.length - 1] ?? draft.pointer;
  const beforeTip = points[points.length - 2] ?? anchor;
  const head = arrowHeadPoints(tip, beforeTip, ARROW_SIZE);

  return (
    <svg
      data-testid="canvas-connector-draft"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: `${CANVAS_WIDTH_PX}px`,
        height: `${CANVAS_HEIGHT_PX}px`,
        overflow: "visible",
        pointerEvents: "none",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    >
      <path
        d={d}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dashed ? "6 6" : undefined}
      />
      {head && (
        <polyline
          points={`${head.left.x},${head.left.y} ${tip.x},${tip.y} ${head.right.x},${head.right.y}`}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {/* 自動作成される予定のオブジェクトの予告表示。 */}
      {ghost && <AutoCreateGhost frame={ghost} source={fromObj} />}
      {/* 接続元アンカー。 */}
      <circle cx={anchor.x} cy={anchor.y} r={4} fill="var(--color-accent)" />
      {/* 接続先候補の 4 辺ドット（スナップ辺を強調）。 */}
      {targetObj &&
        ALL_SIDES.map((side) => {
          const sa = sideAnchor(frameOf(targetObj), side);
          const active = draft.target?.side === side;
          return (
            <circle
              key={side}
              cx={sa.x}
              cy={sa.y}
              r={active ? 6 : 4}
              fill={active ? "var(--color-accent)" : "var(--color-surface)"}
              stroke="var(--color-accent)"
              strokeWidth={2}
            />
          );
        })}
    </svg>
  );
}

/**
 * ゴーストの図形を描くときの線幅。実オブジェクトの描画（objects/shape-object.tsx）と同じく、
 * 永続スキーマが弾く不正な旧データが楽観 state に残っていても線が消えないよう下限を設ける。
 */
function ghostShapeStrokeWidth(strokeWidth: number | undefined): number {
  return Math.max(0.5, strokeWidth ?? SHAPE_DEFAULT_STROKE_WIDTH);
}

/**
 * 自動作成される予定のオブジェクトを半透明で予告表示する。
 *
 * 「離したら何がどこに出るのか」を操作中に伝えるため、枠だけでなく実際に作られる見た目
 * （付箋なら継承する色の紙、図形なら同じ種別・塗り・線色）で描く。
 * 色の既定値は本体の描画コンポーネントから import し、予告と実物が食い違わないようにする。
 * テキストは本文が空だと実物も見えないため、破線の枠だけで示す。
 *
 * 外側の rect は種別によらず必ず描く（配置矩形をそのまま表す要素であり、予告であることを示す
 * 破線の枠でもある）。
 */
function AutoCreateGhost({
  frame,
  source,
}: {
  readonly frame: CanvasFrame;
  readonly source: CanvasObject;
}): JSX.Element {
  // 付箋は継承する色の紙をそのまま半透明で見せる。
  const stickyFill = source.type === "sticky" ? (source.color ?? STICKY_DEFAULT_BACKGROUND) : null;
  return (
    <g pointerEvents="none">
      <rect
        data-testid="connector-auto-create-ghost"
        x={frame.x}
        y={frame.y}
        width={frame.w}
        height={frame.h}
        rx={8}
        fill={stickyFill ?? "var(--color-accent)"}
        fillOpacity={stickyFill ? 0.55 : 0.08}
        stroke="var(--color-accent)"
        strokeWidth={2}
        strokeDasharray="6 6"
      />
      {source.type === "shape" && source.shapeKind !== "arrow" && (
        <g opacity={0.5}>
          {source.shapeKind === "circle" ? (
            <ellipse
              cx={frame.x + frame.w / 2}
              cy={frame.y + frame.h / 2}
              rx={frame.w / 2}
              ry={frame.h / 2}
              fill={source.fill ?? "none"}
              stroke={source.stroke ?? SHAPE_DEFAULT_STROKE}
              strokeWidth={ghostShapeStrokeWidth(source.strokeWidth)}
            />
          ) : (
            <rect
              x={frame.x}
              y={frame.y}
              width={frame.w}
              height={frame.h}
              fill={source.fill ?? "none"}
              stroke={source.stroke ?? SHAPE_DEFAULT_STROKE}
              strokeWidth={ghostShapeStrokeWidth(source.strokeWidth)}
            />
          )}
        </g>
      )}
    </g>
  );
}
